import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { projects } from '../../../mocks/projects';
import { projectDetails } from '../../../mocks/projectDetails';

const FILTERS = ['Tất Cả', 'SỬA - BÁN', 'XÂY - THUÊ', 'VẬN HÀNH'];

export default function ProjectsSection() {
  const [activeFilter, setActiveFilter] = useState('Tất Cả');
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const navigate = useNavigate();

  const filtered = activeFilter === 'Tất Cả'
    ? projects
    : projects.filter((p) => p.type === activeFilter);

  return (
    <section id="projects" className="bg-[#F2F4F5] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <span className="text-[#C5A059] text-xs font-heading font-bold tracking-widest uppercase">
              CASE STUDY THỰC TẾ
            </span>
            <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-[#004D4D] mt-3 leading-tight">
              Kết quả thực tế,<br />không phải lời hứa
            </h2>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2 bg-white rounded-full p-1.5 border border-neutral-light">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setActiveFilter(f)}
                className={`px-5 py-2 rounded-full text-xs font-heading font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeFilter === f
                    ? 'bg-[#004D4D] text-white'
                    : 'text-neutral-dark hover:text-[#004D4D]'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* Project Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((project, index) => {
            const detail = projectDetails.find((d) => d.id === project.id);
            return (
            <div
              key={project.id}
              className={`relative rounded-2xl overflow-hidden cursor-pointer group ${
                index === 0 ? 'md:col-span-2 lg:col-span-1 lg:row-span-2' : ''
              }`}
              onMouseEnter={() => setHoveredId(project.id)}
              onMouseLeave={() => setHoveredId(null)}
              onClick={() => detail && navigate(`/du-an/${detail.slug}`)}
            >
              {/* Image */}
              <div className={`relative overflow-hidden ${index === 0 ? 'h-80 lg:h-[500px]' : 'h-64'}`}>
                <img
                  src={hoveredId === project.id ? project.afterImage : project.beforeImage}
                  alt={project.title}
                  className="w-full h-full object-cover object-top transition-all duration-700"
                />

                {/* Before/After Badge */}
                <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm rounded-full px-3 py-1 flex items-center gap-2">
                  <span className="text-white/70 text-xs">{hoveredId === project.id ? 'SAU' : 'TRƯỚC'}</span>
                  <i className="ri-refresh-line text-white/70 text-xs" />
                  <span className="text-[#C9A961] text-xs font-medium">Hover để xem</span>
                </div>

                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D4D4D]/90 via-transparent to-transparent" />

                {/* Type Tag */}
                <span className="absolute top-4 left-4 bg-[#C9A961] text-[#0D4D4D] text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
                  {project.type}
                </span>

                {/* Info Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-white font-bold text-xl mb-1">{project.title}</h3>
                  <div className="flex items-center gap-1 mb-3">
                    <i className="ri-map-pin-line text-white/70 text-sm" />
                    <span className="text-white/70 text-sm">{project.location}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-white/70">
                        <i className="ri-ruler-line mr-1" />{project.area}
                      </span>
                      <span className="text-white/70">
                        <i className="ri-time-line mr-1" />{project.duration}
                      </span>
                    </div>
                    <span className="text-[#C9A961] font-bold text-lg">{project.result}</span>
                  </div>
                </div>
              </div>
            </div>
            );
          })}
        </div>

        {/* View All Projects Button */}
        <div className="mt-10 flex justify-center">
          <button
            onClick={() => navigate('/du-an')}
            className="inline-flex items-center gap-2.5 bg-white border-2 border-[#004D4D] text-[#004D4D] hover:bg-[#004D4D] hover:text-white font-heading font-bold text-sm px-8 py-4 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap group"
          >
            <i className="ri-gallery-line text-base" />
            Xem Tất Cả Dự Án
            <i className="ri-arrow-right-line text-base transition-transform duration-200 group-hover:translate-x-1" />
          </button>
        </div>

        {/* CTA */}
        <div className="mt-8 bg-[#004D4D] rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <p className="font-heading font-bold text-white text-lg mb-1">
              Xem tài sản của bạn có thể đạt kết quả tương tự
            </p>
            <p className="text-white/60 text-sm font-sans">
              UPLIVING phân tích tiềm năng tài sản miễn phí — không ràng buộc
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <button
              onClick={() => navigate('/bat-mach-tai-san')}
              className="bg-[#C5A059] hover:bg-[#D4B575] text-[#002626] font-heading font-bold text-sm px-7 py-3.5 rounded-full transition-all cursor-pointer whitespace-nowrap"
            >
              Đặt Lịch Bắt Mạch →
            </button>
            <button
              onClick={() => navigate('/du-an')}
              className="bg-white/10 hover:bg-white/20 border border-white/30 text-white font-heading font-semibold text-sm px-7 py-3.5 rounded-full transition-all cursor-pointer whitespace-nowrap"
            >
              Xem Tất Cả Dự Án
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
